import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import {
  CustomCotationsOtherFormData,
  CustomCotationsOtherFormSchema,
} from "@/types/forms/home/customCotationsOtherForm.form";
import { useNavigate } from "react-router-dom";
import Paths from "@/constants/paths";
import { GetAllCompanyDto, GetOneExpeditionResponseDto } from "@/types/api/api-types";
import { useCreateCustomQuotation, useUpdatePendingCustomQuotation } from "@/api/expedition.api";
import { InfoPickup } from "@/components/customCotations/infoPickup";
import { InfoDeliveryOtherType } from "@/components/customCotations/infoDeliveryOtherType";
import { InfoProductOtherType } from "./infoProductOtherType";
import { customQuotationFormDefaultOther } from "./customQuotationFormDefault";
import { InfoValidationOther } from "./infoValidationOther";
import { GetCustomsV1Response } from "@/types/api/api-types";

type Props = {
  company: GetAllCompanyDto;
  customs: GetCustomsV1Response;
  expedition?: GetOneExpeditionResponseDto;
};

export const CustomCotationsOther = ({ company, customs, expedition }: Props) => {
  const navigate = useNavigate();

  const { mutateAsync: createCustomQuotation, isPending: isCreatingCustomQuotation } =
    useCreateCustomQuotation();
  const { mutateAsync: updatePendingCustomQuotation, isPending: isUpdatingCustomQuotation } =
    useUpdatePendingCustomQuotation();

  const isLoading = isCreatingCustomQuotation || isUpdatingCustomQuotation;

  const methods = useForm<CustomCotationsOtherFormData>({
    defaultValues: customQuotationFormDefaultOther(company, expedition),
    resolver: zodResolver(CustomCotationsOtherFormSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const submitForm: SubmitHandler<CustomCotationsOtherFormData> = async (data) => {
    const createCustomCotationData = {
      comment: data.comment,
      type: "OTHER",
      parcelCotation: null,
      paletteCotation: null,
      newFilesId: data.newFilesId || [],
      ...(!data.pickupInfo.manualPickup
        ? {
            pickupLocationId: data.pickupInfo.pickupLocationId,
          }
        : {
            pickupLastName: data.pickupInfo.pickupLastName,
            pickupFirstName: data.pickupInfo.pickupFirstName,
            pickupPhone: data.pickupInfo.pickupPhone,
            pickupCompany: data.pickupInfo.pickupCompany,
            pickupStreet: data.pickupInfo.pickupStreet,
            pickupPostalCode: data.pickupInfo.pickupPostalCode,
            pickupCity: data.pickupInfo.pickupCity,
            pickupCountry: data.pickupInfo.pickupCountry,
          }),
      ...(data.deliveryInfo.singleDelivery
        ? {
            deliveryLastName: data.deliveryInfo.deliveryLastName,
            deliveryFirstName: data.deliveryInfo.deliveryFirstName,
            deliveryPhone: data.deliveryInfo.deliveryPhone,
            deliveryEmail: data.deliveryInfo.deliveryEmail,
            deliveryCompany: data.deliveryInfo.deliveryCompany,
            deliveryStreet: data.deliveryInfo.deliveryStreet,
            deliveryPostalCode: data.deliveryInfo.deliveryPostalCode,
            deliveryCity: data.deliveryInfo.deliveryCity,
            deliveryAdditionalInfo: data.deliveryInfo.deliveryAdditionalInfo,
            deliveryAdditionalInfo2: data.deliveryInfo.deliveryAdditionalInfo2,
            customId: data.deliveryInfo.customId,
          }
        : {
            otherNumberOfExpedition: data.deliveryInfo.otherNumberOfExpedition,
          }),
      billingEntityId: data.billingEntityId,
      insurance: data.insurance,
      express: data.express,
      otherCustomQuotationType: data.otherCustomQuotationType,
      reference: data.reference,
    };

    if (expedition) {
      const ret = await updatePendingCustomQuotation({
        id: expedition.id,
        payload: createCustomCotationData,
      });
      if (ret.status === 200 || ret.status === 201) {
        if (expedition) {
          navigate(Paths.home.pendingShipments);
        } else {
          navigate(Paths.home.cotations);
        }
      }
      return;
    }
    const ret = await createCustomQuotation(createCustomCotationData);
    if (ret.status === 200 || ret.status === 201) navigate(Paths.home.cotations);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submitForm)}
        className="flex flex-1 flex-col items-center gap-6"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <InfoPickup company={company} />
        <InfoDeliveryOtherType customs={customs} />
        <InfoProductOtherType documents={expedition?.expeditionDocuments?.documents || []} />
        <InfoValidationOther
          billingEntities={company?.billingEntities || []}
          isLoading={isLoading}
        />
      </form>
    </FormProvider>
  );
};
